import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ApiFehler } from "../../api/client";
import { usePortalAnmeldungStarten } from "../../api/hooks";
import type { PortalKundeErstellen } from "../../api/typen";
import { Hinweis, Karte, Knopf } from "../../komponenten/Basis";

const LEER = { name: "", email: "", telefon: "", strasse: "", plz: "", ort: "" };
type Werte = typeof LEER;

/** Schritt 1 von 3: nur Name und E-Mail sind Pflicht. Den Rest liest die Plattform aus den Unterlagen. */
export function NeueAnmeldungSeite() {
  const starten = usePortalAnmeldungStarten();
  const navigate = useNavigate();
  const [werte, setWerte] = useState<Werte>(LEER);
  const [fehler, setFehler] = useState<Partial<Record<keyof Werte, string>>>({});
  const [allgemein, setAllgemein] = useState<string | null>(null);

  function setzen(feld: keyof Werte, wert: string) {
    setWerte((alt) => ({ ...alt, [feld]: wert }));
    setFehler((alt) => ({ ...alt, [feld]: undefined }));
  }

  function absenden(e: FormEvent) {
    e.preventDefault();
    setAllgemein(null);
    const neu: Partial<Record<keyof Werte, string>> = {};
    if (werte.name.trim().length < 2) neu.name = "Bitte den Namen des Anlagenbetreibers eingeben.";
    if (!/^\S+@\S+\.\S+$/.test(werte.email.trim())) neu.email = "Bitte eine gültige E-Mail-Adresse eingeben.";
    if (werte.plz.trim() && !/^\d{5}$/.test(werte.plz.trim())) neu.plz = "Die Postleitzahl hat 5 Ziffern.";
    setFehler(neu);
    if (Object.keys(neu).length > 0) return;

    const daten: PortalKundeErstellen = { name: werte.name.trim(), email: werte.email.trim() };
    for (const feld of ["telefon", "strasse", "plz", "ort"] as const) {
      if (werte[feld].trim()) daten[feld] = werte[feld].trim();
    }
    starten.mutate(daten, {
      onSuccess: (anlage) => navigate(`/portal/anmeldung/${anlage.id}`, { replace: true }),
      onError: (err) => {
        if (err instanceof ApiFehler && err.status === 422) {
          const felder = err.feldFehler;
          setFehler({ name: felder.name, email: felder.email, plz: felder.plz });
          if (Object.keys(felder).length === 0) setAllgemein(err.message);
        } else {
          setAllgemein(err instanceof ApiFehler ? err.message : "Das hat nicht geklappt. Bitte erneut versuchen.");
        }
      },
    });
  }

  const feld = (name: keyof Werte, label: string, optionen: { typ?: string; pflicht?: boolean; auto?: string; modus?: "numeric" | "tel" | "email"; hilfe?: string } = {}) => (
    <div>
      <label htmlFor={`f-${name}`} className="mb-1 block text-sm font-semibold">
        {label}
        {optionen.pflicht ? "" : <span className="ml-2 text-xs font-normal text-neutral-500">optional</span>}
      </label>
      <input
        id={`f-${name}`}
        type={optionen.typ ?? "text"}
        inputMode={optionen.modus}
        autoComplete={optionen.auto ?? "off"}
        className={`w-full rounded-xl border px-4 py-3 text-base ${fehler[name] ? "border-ampel-rot" : "border-neutral-300"}`}
        value={werte[name]}
        onChange={(e) => setzen(name, e.target.value)}
        aria-invalid={fehler[name] ? true : undefined}
        aria-describedby={fehler[name] ? `f-${name}-fehler` : undefined}
      />
      {optionen.hilfe && !fehler[name] && <p className="mt-1 text-xs text-neutral-500">{optionen.hilfe}</p>}
      {fehler[name] && <p id={`f-${name}-fehler`} className="mt-1 text-sm text-ampel-rot">{fehler[name]}</p>}
    </div>
  ); // prettier-ignore

  return (
    <div className="space-y-5">
      <div>
        <Link to="/portal" className="text-sm font-semibold text-neutral-600 hover:underline">← Zurück</Link>
        <p className="mt-3 text-sm font-semibold text-neutral-500">Schritt 1 von 3</p>
        <h1 className="text-2xl font-semibold tracking-tight">Wer ist der Anlagenbetreiber?</h1>
      </div>

      <form onSubmit={absenden} noValidate>
        <Karte className="space-y-4 p-4 sm:p-6">
          {feld("name", "Vor- und Nachname", { pflicht: true, auto: "off" })}
          {feld("email", "E-Mail-Adresse", { typ: "email", pflicht: true, modus: "email", hilfe: "Wird für die Anmeldung benötigt." })}
          {feld("telefon", "Telefon", { typ: "tel", modus: "tel" })}
          <div className="border-t border-neutral-100 pt-4">
            <p className="mb-3 text-sm text-neutral-600">Wo steht die Anlage?</p>
            <div className="space-y-4">
              {feld("strasse", "Straße und Hausnummer")}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">{feld("plz", "PLZ", { modus: "numeric" })}</div>
                <div className="col-span-2">{feld("ort", "Ort")}</div>
              </div>
            </div>
          </div>
        </Karte>

        {allgemein && <div className="mt-4"><Hinweis art="fehler">{allgemein}</Hinweis></div>}

        <Knopf variante="primaer" type="submit" laedt={starten.isPending} className="mt-5 w-full !py-4 !text-base">
          Weiter zu den Unterlagen
        </Knopf>
      </form>
    </div>
  );
}
